/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus, SideDrawer, Shopify, theme */

/**
 * Optional cache warming so first drawer open feels instant.
 * Safe no op if requestIdleCallback is not supported.
 */
(function warmCartDrawerScripts() {
  try {
    const ric = window.requestIdleCallback;
    if (!ric) return;

    ric(() => {
      if (window.theme?.scripts?.cartItems) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          loadScript(theme.scripts.cartItems).catch(() => {});
        }
      }

      if (window.theme?.scripts?.countryProvinceSelector) {
        // Only warm this if your drawer can ever show shipping calculator
        // If your dataset flag controls it, we do not know it here, but warming is cheap
        if (!document.querySelector(`script[src="${theme.scripts.countryProvinceSelector}"]`)) {
          loadScript(theme.scripts.countryProvinceSelector).catch(() => {});
        }
      }
    }, { timeout: 1500 });
  } catch (e) {
    // Ignore
  }
})();

/* Cart drawer disclosure */
if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer')?.querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      if (this.overlay) this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      if (this.overlay) this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      if (this.overlay) this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      if (this.overlay) this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* Cart drawer */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.scriptsLoaded = false;
      this.scriptsLoadingPromise = null;

      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (!cartIcon) return;

      cartIcon.setAttribute('role', 'button');
      cartIcon.setAttribute('aria-haspopup', 'dialog');

      cartIcon.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.open(cartIcon);
      });

      cartIcon.addEventListener('keydown', (evt) => {
        if (evt.key !== ' ') return;
        evt.preventDefault();
        this.open(cartIcon);
      });
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '100ms';
        quickAddDrawer.close();
      }

      // Do not add artificial delay unless quick add drawer needed it
      const delay = quickAddDrawer ? 100 : 0;

      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary && this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, delay);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer.
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart.
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Loads enhancements without blocking drawer open.
     * This is the critical change that removes perceived lag.
     */
    ensureEnhancementsLoaded(skipSectionRefresh = false) {
      if (this.scriptsLoaded) return Promise.resolve();
      if (this.scriptsLoadingPromise) return this.scriptsLoadingPromise;

      this.scriptsLoadingPromise = (async () => {
        const tasks = [];

        // cart items script
        if (window.theme?.scripts?.cartItems) {
          if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
            tasks.push(loadScript(theme.scripts.cartItems));
          }
        }

        // shipping calculator scripts
        if (this.dataset.shippingCalculator) {
          if (window.theme?.scripts?.countryProvinceSelector) {
            if (!document.querySelector(`script[src="${theme.scripts.countryProvinceSelector}"]`)) {
              tasks.push(loadScript(theme.scripts.countryProvinceSelector));
            }
          }

          if (window.theme?.scripts?.shippingCalculator) {
            // If shipping calculator depends on countryProvinceSelector being present,
            // wait for the tasks already queued first.
            tasks.push((async () => {
              if (tasks.length) await Promise.all(tasks);
              if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`)) {
                await loadScript(theme.scripts.shippingCalculator);
              }
            })());
          }
        }

        if (tasks.length) await Promise.all(tasks);

        this.scriptsLoaded = true;

        // Let newly loaded components bind then refresh. When the caller has already
        // rendered fresh section HTML (renderContents), the refresh only needs to
        // resync recommendations — re-fetching the sections would be a redundant
        // serial round trip on the add-to-cart path.
        const cartItems = this.querySelector('cart-items');
        if (cartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh(skipSectionRefresh);
        }

        CartDrawer.recalculateCssVarHeights();
      })();

      return this.scriptsLoadingPromise;
    }

    /**
     * Renders the contents of the specified sections to update.
     * Drawer opens immediately, then scripts load after.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      // Open immediately for perceived speed
      if (openDrawer && this.getAttribute('open') === null) {
        requestAnimationFrame(() => this.open());
      }

      // Render HTML immediately
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      // Recalc heights quickly
      CartDrawer.recalculateCssVarHeights();

      // Load enhancements after open and render, without blocking UX. The sections
      // were just rendered above, so skip the enhancement pass's own re-fetch.
      this.ensureEnhancementsLoaded(true).catch((e) => console.log(e));
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data css var heights on the page.
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}